import { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from "react-redux"
import { createCardThunk } from "../thunks/card";
import { Accordion } from './Accordion';
import { DebugToolCreatedCardSnippet } from './debug/DebugToolCreatedCardSnippet';
import { DebugToolCustomerSnippet } from './debug/DebugToolCustomerSnippet';
import DebugToolModalButton from './debug/DebugToolModalButton';
import { DebugToolPaymentTokenSnippet } from './debug/DebugToolPaymentTokenSnippet';
import Prompt from './Prompt';

interface AddNewCardHtmlForm extends EventTarget {
    cardNumber: { value: string }
    expDate: { value: string }
    cvc: { value: string }
    isDefault: { checked: boolean }
}

function AddNewCardForm () {
    const currentUser: User = useSelector((state: AppState) => state.currentUser)
    const defaultCard: string = useSelector((state: AppState) => state.defaultCard)
    const apiError: any = useSelector((state: AppState) => state.apiError)
    const [isLoading, setIsLoading] = useState(false)
    const [cardNumber, setCardNumber] = useState('')
    const [cvc, setCVC] = useState('')
    const [expDate, setExpDate] = useState('')
    
    const dispatch: Dispatch<any> = useDispatch()

    useEffect(() => {
        setIsLoading(false)
    }, [defaultCard, apiError])

    const onAddCard = async (e: React.SyntheticEvent) => {
        e.preventDefault()
       
        const form = e.target as AddNewCardHtmlForm

        // STEP 1: COLLECT FORM VALUES
        const newCardDetails: NewCardDetails = {
            number: form.cardNumber.value.split(' ').join(''),
            expMonth: form.expDate.value.split('/')[0],
            expYear: `20${form.expDate.value.split('/')[1]}`,
            cvc: form.cvc.value,
            isDefault: form.isDefault.checked
        }

        // STEP 2: CREATE CARD - PASS NEW CARD DETAILS AS PARAMETER
        dispatch(createCardThunk(currentUser, newCardDetails))
        setIsLoading(true)
        
    }

    const handleCardNumberChange = (e: any) => {
        const value: string = e.target.value
        if (/^[0-9\s]*$|^$/.test(value)) {
            setCardNumber(value)
        }
    }
    
    const formatCardNumber = (e: any) => {
        const value = e.target.value
        const count = value.length 
        
        // for every 4 numbers and backspace is not pressed, append space
        if (count < 19 && count % 5 === 4 && e.keyCode !== 8) {
            setCardNumber(`${value.substring(0, count)} `)
        } else {
            setCardNumber(value)
        }

        // if backspace is pressed
        if (count > 0 && count % 5 === 0) {
            if (e.keyCode === 8 && value.slice(-1) === ' ') {
                setCardNumber(value.substring(0, count - 1))
            } else {
                // auto insert space after 4 numbers
                setCardNumber(`${value.substring(0, count - 1)} ${value.substring(count - 1)}`)
            }
        }
    }

    const validateCVC = (e: any) => {
        const value: string = e.target.value
        
        if(/\d$|^$/.test(value)) {
            setCVC(value)
        }
    }

    const handleExpDateChange = (e: any) => {
        const value: string = e.target.value
        const lastChar = value.slice(-1)

        if(/^\d$|^\/|^$/.test(lastChar)) {
            // accept at most only 1 /
            if ((value.match(/\//g) || []).length <= 1) {
                setExpDate(value)
            }
        }
    }

    const formatExpDate = (e: any) => {
        const value = e.target.value
        const count = value.length
        
        // if 2 numbers and backspace is not pressed, append /
        if (/^\d{2}$/.test(value) && e.keyCode !== 8) {
            setExpDate(`${value}/`)
        }

        // if no year
        if (count === 3) {
            // if backspace is pressed, remove / 
            if (e.keyCode === 8) {
                setExpDate(value.replace('/', ''))
            } else {
                // if no / yet, insert / 
                if (!value.includes('/')) {
                    setExpDate(`${value.substring(0, 2)}/${value.substring(2)}`)
                }
            }
        }
    }

    return (
        <form className="add-new-card-form" onSubmit={(e: React.SyntheticEvent) => onAddCard(e)}>
            <input 
                type="text" 
                name="cardNumber" 
                placeholder="Card Number" 
                maxLength={19}
                onKeyUp={(e) => formatCardNumber(e)}
                onChange={(e) => handleCardNumberChange(e)}
                value={cardNumber}
                required 
            />
            <input 
                type="text"
                name="expDate"
                placeholder="Date (MM/YY)"
                minLength={5}
                maxLength={5}
                onKeyUp={(e) => formatExpDate(e)}
                onChange={(e) => handleExpDateChange(e)}
                value={expDate}
                required 
            />
            <input 
                type="text" 
                name="cvc" 
                placeholder="CVC"
                maxLength={4}
                onChange={(e) => validateCVC(e)}
                value={cvc}
                required 
            />
            <div>
                <input type="checkbox" id="isDefault" name="isDefault" defaultChecked={true} disabled />
                <label className="label-default" htmlFor='isDefault'> Set as default </label>
            </div>
            <Prompt text="📨 &ensp; We will be charging Php 10 to the card. This amount will be automatically refunded back." />
            <div className='btn-container'>
                {/* DEBUG TOOL */}
                <DebugToolModalButton header='Debug Tool' className='btn orange debug'>
                    <Accordion header='Create Customer Payload' index='1' group='debug' type='checkbox' defaultChecked={true}>
                        <DebugToolCustomerSnippet/>
                    </Accordion>
                    <Accordion header='Create Payment Token Payload' index='2' group='debug' type='checkbox' defaultChecked={true}>
                        <DebugToolPaymentTokenSnippet />
                    </Accordion>
                    <Accordion header='Create Card Payload' index='3' group='debug' type='checkbox' defaultChecked={true}>
                        <DebugToolCreatedCardSnippet />
                    </Accordion>
                </DebugToolModalButton>
                <button type="submit" className="btn green"> { isLoading ? <div className='bar-loader'/> : 'Save' } </button>
            </div>
        </form>
   )
}

export default AddNewCardForm